/**
 * CSV export helpers shared by the list views.
 * UTF-8 BOM + semicolon separator so Excel (es-ES) opens files directly.
 */

export interface CsvColumn {
  source: string;
  label: string;
}

/** Prevent spreadsheet formula injection from API-provided strings */
export const escapeCsvCell = (raw: string): string => {
  return /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
};

// Resolve a column value from a row. Most lists key their columns with flat
// names, but some flatten joined relations under dotted source paths (e.g.
// 'staff.full_name'); fall back to a nested lookup so those export correctly.
const resolveValue = (record: Record<string, any>, source: string): any => {
  if (record[source] !== undefined) return record[source];
  if (!source.includes('.')) return record[source];
  return source
    .split('.')
    .reduce<any>((acc, key) => (acc == null ? acc : acc[key]), record);
};

const cellValue = (record: Record<string, any>, source: string): string => {
  const value = resolveValue(record, source);
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return escapeCsvCell(value.join(', '));
  if (typeof value === 'object') return escapeCsvCell(JSON.stringify(value));
  return escapeCsvCell(String(value));
};

export const downloadCsv = (
  rows: Record<string, any>[],
  columns: CsvColumn[],
  filename: string
): void => {
  const header = columns.map((column) => column.label).join(';');
  const lines = rows.map((row) =>
    columns
      .map((column) => `"${cellValue(row, column.source).replace(/"/g, '""')}"`)
      .join(';')
  );
  const csv = '﻿' + [header, ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
